import { ChangeEventHandler, FocusEventHandler, forwardRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';

import { Input } from '../Input/Input/Input';
import { TimestampDateInput } from '../TimestampDateInput/TimestampDateInput';

type SchemableFormInputProps = Omit<Parameters<typeof Input>[0], 'errors' | 'hasError'> & {
  error: WebEDIError | undefined;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  totalNumberOfErrors: number;
};
export const SchemableFormInput = forwardRef<HTMLInputElement, SchemableFormInputProps>(
  (
    {
      error,
      totalNumberOfErrors,
      onBlur,
      onShowFieldErrorModal,
      description,
      variant,
      valueDescription,
      fieldState,
      ...otherProps
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isValidating = useWebEDIStore((state) => state.isValidating);
    const { ErrorModalComponent, useErrorModal } = useErrorContext();
    const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
    const onInputFocus = useCallback(() => {
      setShowErrorModal(true);
    }, [setShowErrorModal]);

    const onInputBlur: FocusEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        onBlur?.(e);
        setShowErrorModal(false);
      },
      [onBlur, setShowErrorModal],
    );

    const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        otherProps.onChange?.(event);
      },
      [otherProps],
    );

    useEffect(() => {
      if (error && showErrorModal) {
        onShowFieldErrorModal?.(true);
      }
    }, [error, onShowFieldErrorModal, showErrorModal]);

    const InputComponent = otherProps.type === 'date' ? TimestampDateInput : Input;
    if (!otherProps.name) {
      return null;
    }

    const isLargeVariant = variant === 'large';
    const inlineErrors =
      isLargeVariant && error?.description ? { message: error.description, type: error.type ?? 'error' } : undefined;

    return (
      <div>
        <InputComponent
          {...otherProps}
          value={otherProps.value ?? ''}
          placeholder={
            !otherProps.value && error?.type === 'required' ? t('common:table.errors.requiredField') : undefined
          }
          onChange={handleOnChange}
          ref={ref}
          wrapperRef={isLargeVariant ? undefined : refs.setReference}
          description={isLargeVariant ? description : undefined}
          errors={inlineErrors}
          fieldState={fieldState}
          onFocus={isLargeVariant ? undefined : onInputFocus}
          onBlur={isLargeVariant ? onBlur : onInputBlur}
          name={otherProps.name}
          data-field={otherProps.name}
          min={otherProps.type === 'number' ? 0 : undefined}
          data-1p-ignore={true}
          {...(isLargeVariant ? { variant, valueDescription } : { variant: 'default' })}
        />

        {!isLargeVariant && showErrorModal && ErrorModalComponent && (
          <ErrorModalComponent
            viewMode={false}
            error={error}
            ref={refs.setFloating}
            style={floatingStyles}
            numberOfErrors={totalNumberOfErrors}
            isValidating={isValidating}
            fieldDescription={description}
            isPendingValidation={fieldState === 'pending-validation'}
          />
        )}
      </div>
    );
  },
);

SchemableFormInput.displayName = 'SchemableFormInput';
